import {
  QueryClient,
  UseMutateAsyncFunction,
  useQueryClient,
} from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { useCallback, useState } from "react";
import {
  MarkReleaseTrackFileParams,
  useMarkReleaseTrackFileAsUploaded,
} from "../api/files/hooks/useMarkReleaseTrackFileAsUploaded";
import { useUploadReleaseTrack } from "../api/releases/hooks/useUploadReleaseTrack";
import {
  UploadReleaseTrackArgs,
  UploadReleaseTrackResponse,
} from "../api/releases/uploadReleaseTrackAPI";
import {
  SupplyChainFileAction,
  SupplyChainFileType,
  SupplyChainRelatedType,
} from "../api/supplyChain/postSupplyChainUpdateFile";
import { SupplyChainFileAxios } from "../api/supplyChain/supplyChainAxiosInstance";
import {
  FinalizeMultipartUploadResponse,
  supplyChainUploadFileToS3,
} from "../api/supplyChain/supplyChainUploadFile";
import { removeFailedUploadAtom } from "../atoms/supplychain/supplyChainFailedUploadsAtom";
import { updateUploadProgressAtom } from "../atoms/supplychain/supplyChainMultiUploadProgressAtom";
import {
  addPendingUploadAtom,
  PendingUploadData,
  PendingUploadParams,
  SupplyChainUploadMetadata,
} from "../atoms/supplychain/supplyChainPendingUploadsAtom";
import { SUPPLY_CHAIN_ACTIVE } from "../constants/generated";
import { QUERY_KEYS } from "../constants/queryKeys";
import { downloadTrackRequest } from "../store/actions/audioService";
import { MultipartUploadParams } from "../store/actions/fileVersions";
import { FileVersion } from "../store/models/fileVersion";
import { ReleaseAndReleaseTracks, ReleaseTrack } from "../store/models/release";
import { handleSingleOrMultipartUpload } from "./fileUtils";
import { readStream } from "../store/utils/fetch";

/**
 * A non-stateful helper function for handling release track file uploads.
 *
 * @param acceptedFiles - The files to upload.
 * @param uploadReleaseTrack - The mutation function for uploading the release track.
 * @param markReleaseTrackFileAsUploaded - The mutation function for marking the release track file as uploaded.
 * @param setProgressState - A callback function for updating the progress state.
 * @param setAbortController - A callback function for updating the abort controller state.
 */
export const handleReleaseTrackFileUpload = async (
  acceptedFiles: File[],
  uploadReleaseTrack: UseMutateAsyncFunction<
    UploadReleaseTrackResponse,
    Error,
    UploadReleaseTrackArgs,
    unknown
  >,
  markReleaseTrackFileAsUploaded: UseMutateAsyncFunction<
    FileVersion,
    Error,
    MarkReleaseTrackFileParams,
    unknown
  >,
  addPendingUpload: (data: PendingUploadParams) => void,
  setProgressState: (progress: number) => void,
  setAbortController: (controller: AbortController | null) => void,
  setIsLoading: (isLoading: boolean) => void,
  removeFailedUpload: (releaseTrackId: number) => void,
): Promise<{
  success: boolean;
  fileVersion?: FileVersion;
  releaseTrackData?: UploadReleaseTrackResponse;
}> => {
  const file = acceptedFiles[0];
  setIsLoading(true);
  try {
    // Uploading to EngineEars.
    const releaseTrackData = await uploadReleaseTrack({ file });
    const controller = new AbortController();
    setAbortController(controller);
    const {
      key,
      bucket,
      multipart_upload_chunk_size,
      multipart_upload_id,
      multipart_upload_urls,
      signed_url,
      file_version_id,
    } = releaseTrackData;

    const uploadFiles: [
      Promise<MultipartUploadParams | null>,
      Promise<FinalizeMultipartUploadResponse>,
    ] = [
      // Upload to EngineEars.
      handleSingleOrMultipartUpload(
        file,
        key,
        signed_url,
        multipart_upload_id,
        multipart_upload_urls,
        multipart_upload_chunk_size,
        bucket,
        // SupplyChain upload is slower, so track that.
        undefined,
        controller.signal,
      ),
      // Upload to SupplyChain.
      supplyChainUploadFileToS3(
        file,
        SupplyChainFileType.AUDIO,
        SupplyChainRelatedType.Product,
        SupplyChainFileAxios.client,
        setProgressState,
        controller.signal,
      ),
    ];

    const [multipartParams, { finalFilePath, signedUrl }] =
      await Promise.all(uploadFiles);

    const fileVersion = await markReleaseTrackFileAsUploaded({
      fileVersionId: file_version_id,
      args: multipartParams || undefined,
    });

    const supplyChainUploadData: SupplyChainUploadMetadata = {
      type: SupplyChainFileType.AUDIO,
      action: releaseTrackData.supply_chain_file_id
        ? SupplyChainFileAction.UPDATE
        : SupplyChainFileAction.INSERT,
      name: signedUrl,
      original_file_name: file.name,
      filePath: finalFilePath,
      ...(releaseTrackData.supply_chain_file_id
        ? { id: releaseTrackData.supply_chain_file_id }
        : {}),
    };

    const pendingUploadData: PendingUploadData = {
      releaseId: releaseTrackData.release,
      productId: releaseTrackData.supply_chain_product_id,
      fileVersionId: file_version_id,
      supplyChainUploadMetadata: supplyChainUploadData,
      releaseTrackId: releaseTrackData.id,
    };

    addPendingUpload({ key: finalFilePath, pendingUploadData });
    removeFailedUpload(releaseTrackData.id);

    return { success: true, fileVersion, releaseTrackData };
  } catch (error) {
    if (error instanceof Error && error.message === "Upload was aborted") {
      console.info("Upload cancelled by user");
    }
    return { success: false };
  } finally {
    setProgressState(0);
    setAbortController(null);
    setIsLoading(false);
  }
};

export const handleEngineEarsOnlyTrackFileUpload = async (
  acceptedFiles: File[],
  uploadReleaseTrack: UseMutateAsyncFunction<
    UploadReleaseTrackResponse,
    Error,
    UploadReleaseTrackArgs,
    unknown
  >,
  markReleaseTrackFileAsUploaded: UseMutateAsyncFunction<
    FileVersion,
    Error,
    MarkReleaseTrackFileParams,
    unknown
  >,
  setProgressState: (progress: number) => void,
  setAbortController: (controller: AbortController | null) => void,
  setIsLoading: (isLoading: boolean) => void,
  queryClient: QueryClient,
): Promise<{
  success: boolean;
  fileVersion?: FileVersion;
  releaseTrackData?: UploadReleaseTrackResponse;
}> => {
  const file = acceptedFiles[0];
  setIsLoading(true);
  try {
    // Uploading to EngineEars.
    const releaseTrackData = await uploadReleaseTrack({ file });
    const controller = new AbortController();
    setAbortController(controller);
    const {
      key,
      bucket,
      multipart_upload_chunk_size,
      multipart_upload_id,
      multipart_upload_urls,
      signed_url,
      file_version_id,
    } = releaseTrackData;

    const multipartParams = await handleSingleOrMultipartUpload(
      file,
      key,
      signed_url,
      multipart_upload_id,
      multipart_upload_urls,
      multipart_upload_chunk_size,
      bucket,
      setProgressState,
      controller.signal,
    );

    const fileVersion = await markReleaseTrackFileAsUploaded({
      fileVersionId: file_version_id,
      args: multipartParams || undefined,
    });

    void queryClient.invalidateQueries({
      queryKey: [
        QUERY_KEYS.FETCH_ARTIST_RELEASE_TRACKS,
        releaseTrackData.release,
      ],
    });

    return { success: true, fileVersion, releaseTrackData };
  } catch (error) {
    if (error instanceof Error && error.message === "Upload was aborted") {
      console.info("Upload cancelled by user");
    }
    return { success: false };
  } finally {
    setProgressState(0);
    setAbortController(null);
    setIsLoading(false);
  }
};

/**
 * A stateful helper function for handling release track file uploads.
 * This function handles the state management of the upload process, including
 * progress, aborting the upload, and all the various Tanstack Mutations required.
 *
 * @param releaseId - The ID of the release to upload the track for.
 * @returns A stateful version of the handleReleaseTrackFileUpload function.
 */
export const useHandleReleaseTrackFileUpload = (releaseId: number) => {
  const [progressState, setProgressState] = useState(0);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { mutateAsync: uploadReleaseTrack } = useUploadReleaseTrack(releaseId);
  const { mutateAsync: markReleaseTrackFileAsUploaded } =
    useMarkReleaseTrackFileAsUploaded();
  const addPendingUpload = useSetAtom(addPendingUploadAtom);
  const removeFailedUpload = useSetAtom(removeFailedUploadAtom);

  const queryClient = useQueryClient();

  const handleCancelUpload = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setProgressState(0);
      setAbortController(null);
    }
    void queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.FETCH_ARTIST_RELEASE_TRACKS, releaseId],
    });
  }, [abortController, releaseId, queryClient]);

  const handleUpload = useCallback(
    async (files: File[]) => {
      // Only upload to supply chain if integration is active.
      if (SUPPLY_CHAIN_ACTIVE) {
        return handleReleaseTrackFileUpload(
          files,
          uploadReleaseTrack,
          markReleaseTrackFileAsUploaded,
          addPendingUpload,
          setProgressState,
          setAbortController,
          setIsLoading,
          removeFailedUpload,
        );
      } else {
        return handleEngineEarsOnlyTrackFileUpload(
          files,
          uploadReleaseTrack,
          markReleaseTrackFileAsUploaded,
          setProgressState,
          setAbortController,
          setIsLoading,
          queryClient,
        );
      }
    },
    [
      uploadReleaseTrack,
      markReleaseTrackFileAsUploaded,
      addPendingUpload,
      queryClient,
      removeFailedUpload,
    ],
  );

  return {
    handleUpload,
    handleCancelUpload,
    progressState,
    isPending: isLoading,
  };
};

export const useHandleProjectTracksFileUpload = () => {
  const addPendingUpload = useSetAtom(addPendingUploadAtom);
  const removeFailedUpload = useSetAtom(removeFailedUploadAtom);
  const setUploadProgress = useSetAtom(updateUploadProgressAtom);
  const setProgressStateBuilder = useCallback(
    (releaseTrackId: number) => {
      return (percent: number) => {
        setUploadProgress({ releaseTrackId, percent });
      };
    },
    [setUploadProgress],
  );

  const handleProjectUploads: (
    releaseId: number,
    releaseTracks: ReleaseTrack[],
  ) => Promise<void> = useCallback(
    async (releaseId: number, releaseTracks: ReleaseTrack[]) => {
      return uploadExistingTracks(
        releaseId,
        releaseTracks,
        addPendingUpload,
        setProgressStateBuilder,
        removeFailedUpload,
      );
    },
    [addPendingUpload, setProgressStateBuilder, removeFailedUpload],
  );

  return {
    handleProjectUploads,
  };
};

const uploadExistingTracks = (
  releaseId: number,
  releaseTracks: ReleaseTrack[],
  addPendingUpload: (data: PendingUploadParams) => void,
  setProgressStateBuilder: (
    releaseTrackId: number,
  ) => (percent: number) => void,
  removeFailedUpload: (releaseTrackId: number) => void,
) => {
  const tracksToUpload = releaseTracks.filter(
    (track) =>
      track.project_id && track.file_version_id && !track.supply_chain_file_id,
  );

  const promises: Promise<void>[] = [];

  tracksToUpload.forEach((track) => {
    const setProgressState = setProgressStateBuilder(track.id);
    setProgressState(0);
    promises.push(
      uploadExistingTrack(
        releaseId,
        track,
        addPendingUpload,
        setProgressState,
        removeFailedUpload,
      ),
    );
  });

  void Promise.all(promises);
};

const uploadExistingTrack = async (
  releaseId: number,
  releaseTrack: ReleaseTrack,
  addPendingUpload: (data: PendingUploadParams) => void,
  setProgressState: (percent: number) => void,
  removeFailedUpload: (releaseTrackId: number) => void,
) => {
  if (!releaseTrack.file_version_id) return;

  const blob = await downloadTrackRequest(releaseTrack.file_version_id);
  const file = new File(
    [blob],
    releaseTrack.file_version_id.toString() + ".wav",
    {
      type: blob.type,
    },
  );

  const { finalFilePath, signedUrl } = await supplyChainUploadFileToS3(
    file,
    SupplyChainFileType.AUDIO,
    SupplyChainRelatedType.Product,
    SupplyChainFileAxios.client,
    setProgressState,
  );

  const supplyChainUploadData: SupplyChainUploadMetadata = {
    type: SupplyChainFileType.AUDIO,
    action: SupplyChainFileAction.INSERT,
    name: signedUrl,
    original_file_name: file.name,
    filePath: finalFilePath,
  };

  const pendingUploadData: PendingUploadData = {
    releaseId: releaseId,
    productId: releaseTrack.supply_chain_product_id || -1,
    fileVersionId: releaseTrack.file_version_id,
    supplyChainUploadMetadata: supplyChainUploadData,
    releaseTrackId: releaseTrack.id,
    projectId: releaseTrack.project_id,
  };

  addPendingUpload({ key: finalFilePath, pendingUploadData });
  removeFailedUpload(releaseTrack.id);
};

const uploadCoverPhoto = async (
  release: ReleaseAndReleaseTracks,
  addPendingUpload: (data: PendingUploadParams) => void,
) => {
  const coverPhoto = release.release_cover;
  if (!coverPhoto) return;
  const coverPhotoBlob = await readStream(coverPhoto.url);
  const coverPhotoFile = new File(
    [coverPhotoBlob],
    release.id.toString() + "-cover-photo.jpeg",
    {
      type: coverPhotoBlob.type,
    },
  );

  const {
    finalFilePath,
    signedUrl: supplyChainSignedUrl,
  }: FinalizeMultipartUploadResponse = await supplyChainUploadFileToS3(
    coverPhotoFile,
    SupplyChainFileType.IMAGE,
    SupplyChainRelatedType.Product,
    SupplyChainFileAxios.client,
  );

  const supplyChainUploadData: SupplyChainUploadMetadata = {
    type: SupplyChainFileType.IMAGE,
    action: release.supply_chain_file_id
      ? SupplyChainFileAction.UPDATE
      : SupplyChainFileAction.INSERT,
    name: supplyChainSignedUrl,
    original_file_name: coverPhotoFile.name,
    filePath: finalFilePath,
    ...(release.supply_chain_file_id
      ? { id: release.supply_chain_file_id }
      : {}),
  };

  const pendingUploadData: PendingUploadData = {
    productId: release.supply_chain_product_id,
    releaseId: release.id,
    supplyChainUploadMetadata: supplyChainUploadData,
  };
  addPendingUpload({ key: finalFilePath, pendingUploadData });
};

export const useUploadFilesForStreaming = () => {
  const addPendingUpload = useSetAtom(addPendingUploadAtom);
  const removeFailedUpload = useSetAtom(removeFailedUploadAtom);
  const setUploadProgress = useSetAtom(updateUploadProgressAtom);
  const setProgressStateBuilder = useCallback(
    (releaseTrackId: number) => {
      return (percent: number) => {
        setUploadProgress({ releaseTrackId, percent });
      };
    },
    [setUploadProgress],
  );

  const uploadFilesForStreaming = async (release: ReleaseAndReleaseTracks) => {
    const promises: Promise<void>[] = [];

    if (release.release_cover?.id && !release.supply_chain_file_id) {
      promises.push(uploadCoverPhoto(release, addPendingUpload));
    }

    const tracksToUpload = release.releasetrack_set.filter(
      (track) => !track.supply_chain_file_id,
    );

    tracksToUpload.forEach((track) => {
      const setProgressState = setProgressStateBuilder(track.id);
      setProgressState(0);
      promises.push(
        uploadExistingTrack(
          release.id,
          track,
          addPendingUpload,
          setProgressState,
          removeFailedUpload,
        ),
      );
    });

    void Promise.all(promises);
  };

  return { uploadFilesForStreaming };
};
